import { Flag } from '../components/shared/Flag';
/**
 * DemoLoginPage — Login simplificado para el tenant demo.
 *
 * Muestra directamente los roles disponibles para explorar el demo —
 * el backend (handleDemoAuth) solo necesita el rol, no un "usuario demo".
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router';
import { Button, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import {
  Person24Regular,
  LeafTwo24Regular,
  PeopleTeam24Regular,
  BoxMultiple24Regular,
  People24Regular,
} from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/auth.store';
import { useTenantStore } from '../store/tenant.store';
import { useLocaleStore, type Locale } from '../store/locale.store';
import { LOCALE_OPTIONS, useT } from '../i18n';
import { usePageTitle } from '../hooks/usePageTitle';
import type { Role } from '../types/api.types';

const DEMO_TENANT = 'demo';

const DEMO_ROLES: Role[] = ['FIELD_WORKER', 'SUPERVISOR', 'WAREHOUSE_MANAGER', 'AGRONOMIST', 'INVESTOR', 'ADMIN'];

const ROLE_ICON_MAP: Record<Role, typeof Person24Regular> = {
  'ADMIN': Person24Regular,
  'AGRONOMIST': LeafTwo24Regular,
  'SUPERVISOR': PeopleTeam24Regular,
  'WAREHOUSE_MANAGER': BoxMultiple24Regular,
  'FIELD_WORKER': People24Regular,
  'SUPER_ADMIN': Person24Regular,
  'AUDITOR': Person24Regular,
  'HR': People24Regular,
  'GUARD': Person24Regular,
  'INVESTOR': Person24Regular,
};

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: '40px',
    width: '420px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: tokens.shadow16,
    alignItems: 'center',
  },
  badge: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '2px 10px',
    fontSize: '12px',
    fontWeight: 600,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    textAlign: 'center',
  },
  roleGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  roleCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    transition: 'border-color 0.15s, background 0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  roleCardSelected: {
    border: `1px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  roleIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  roleText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  roleLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  roleDesc: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  error: { color: tokens.colorStatusDangerForeground1, fontSize: '13px', textAlign: 'center' },
});

export function DemoLoginPage() {
  const s = useStyles();
  const t = useT();
  usePageTitle(t.pageTitle.demoLogin);
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId?: string }>();
  const { setSession } = useAuthStore();
  const { setTenant } = useTenantStore();
  const { locale, setLocale } = useLocaleStore();

  const [selectedRole, setSelectedRole] = useState<Role | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // null = comprobando; false = demo apagado (no se ofrecen roles).
  const [available, setAvailable] = useState<boolean | null>(null);
  useEffect(() => {
    apiClient.get('/api/auth/demo?probe=1')
      .then(() => setAvailable(true))
      .catch(err => setAvailable(err?.response?.status === 403 ? false : true));
  }, []);

  // Accept both /demo/login (no tenantId) and /:tenantId/login with tenantId='demo'
  const demoId = tenantId || DEMO_TENANT;
  if (demoId !== DEMO_TENANT) {
    return <Navigate to="/login" replace />;
  }

  const handleEnter = async (roleOverride?: string) => {
    const role = roleOverride ?? selectedRole;
    if (!role) return;

    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get(`/api/auth/demo?role=${role}`);
      setSession(res.data.token, res.data.user);
      setTenant(demoId, 'Empresa Demo');
      navigate(`/${demoId}/dashboard`, { replace: true });
    } catch {
      setError(t.demoLogin.connectError);
    } finally {
      setLoading(false);
    }
  };

  if (available === false) {
    return (
      <div className={s.root}>
        <div className={s.card}>
          <img src="/agrovid-logo.svg" alt="Agrovid" style={{ height: '84px', width: 'auto' }} />
          <span className={s.badge}>DEMO</span>
          <Text className={s.subtitle}>{t.demoLogin.unavailable}</Text>
          <Button appearance="primary" onClick={() => navigate('/login', { replace: true })}>
            {t.demoLogin.goToLogin}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.root}>
      <div className={s.card}>
        <img src="/agrovid-logo.svg" alt="Agrovid" style={{ height: '84px', width: 'auto' }} />

        <span className={s.badge}>DEMO</span>

        <Text className={s.subtitle}>
          {t.demoLogin.subtitle}
        </Text>

        <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
          {LOCALE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setLocale(opt.value as Locale)}
              aria-label={opt.label}
              aria-pressed={locale === opt.value}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '4px 10px', borderRadius: '999px', cursor: 'pointer',
                fontSize: '12px', fontWeight: locale === opt.value ? 600 : 400,
                border: `1px solid ${locale === opt.value ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke2}`,
                background: locale === opt.value ? tokens.colorBrandBackground2 : 'transparent',
                color: locale === opt.value ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3,
              }}
            >
              <Flag iso={opt.flagIso} />
              <span>{opt.value.toUpperCase()}</span>
            </button>
          ))}
        </div>

        <div className={s.roleGrid}>
          {DEMO_ROLES.map(role => {
            const Icon = ROLE_ICON_MAP[role];
            return (
              <div
                key={role}
                className={`${s.roleCard} ${selectedRole === role ? s.roleCardSelected : ''}`}
                onClick={(e) => {
                  // Ctrl/⌘ + clic en Administrador: entrar como SUPER_ADMIN (el backend exige serlo ya).
                  if ((e.ctrlKey || e.metaKey) && role === 'ADMIN') return void handleEnter('SUPER_ADMIN');
                  setSelectedRole(role);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setSelectedRole(role)}
              >
                <Icon className={s.roleIcon} />
                <div className={s.roleText}>
                  <span className={s.roleLabel}>{(t.demoLogin.roleLabels as Record<string, string>)[role]}</span>
                  <span className={s.roleDesc}>{(t.demoLogin.roleDesc as Record<string, string>)[role]}</span>
                </div>
              </div>
            );
          })}
        </div>

        {loading
          ? <Spinner size="medium" />
          : (
            <Button
              appearance="primary"
              size="large"
              onClick={() => handleEnter()}
              style={{ width: '100%' }}
              disabled={!selectedRole}
            >
              {t.demoLogin.enterAs} {selectedRole ? (t.demoLogin.roleLabels as Record<string, string>)[selectedRole] : '...'}
            </Button>
          )
        }

        {error && <div className={s.error}>{error}</div>}
      </div>

    </div>
  );
}
